// BackupsPanel — one environment's backups: the ledger, the night's verdict,
// "back up now", and "restore" — which states its price in rows before asking
// for anything (design_backups_study.md §7.4, §11). Nothing here computes a
// decision: the server states the price and runs the restore under the glass.
import { useCallback, useEffect, useState } from "react";
import { listBackups, takeBackup, restoreBackup, type BackupsLedger, type BackupManifest, type SecretsEnv } from "../api";
import { backupWhen, userRows, nightVerdict, REASON_LABEL } from "../lib/backups_view";

export default function BackupsPanel({ slug }: { slug: string }) {
  const [env, setEnv] = useState<SecretsEnv>("dev");
  const [ledger, setLedger] = useState<BackupsLedger | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);        // what is running: "backup" | a stamp
  const [pending, setPending] = useState<{ stamp: string; warning: string } | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLedger(null); setError(null);
    listBackups(slug, env).then(setLedger).catch((e) => setError(String(e?.message ?? e)));
  }, [slug, env]);
  useEffect(() => { reload(); }, [reload]);

  async function backupNow() {
    setBusy("backup"); setError(null); setDone(null);
    try { const r = await takeBackup(slug, env); setDone(r.ok ? `Backed up — ${r.manifest?.stamp}` : r.error ?? "failed"); reload(); }
    catch (e: any) { setError(String(e?.message ?? e)); }
    finally { setBusy(null); }
  }
  async function askRestore(stamp: string) {
    setBusy(stamp); setError(null); setDone(null);
    try {
      await restoreBackup(slug, env, stamp, false);               // the server always answers 409 here — with the price
    } catch (e: any) {
      if (e?.status === 409 && e?.body?.warning) setPending({ stamp, warning: e.body.warning });
      else setError(String(e?.message ?? e));
    } finally { setBusy(null); }
  }
  async function confirmRestore() {
    if (!pending) return;
    setBusy(pending.stamp); setError(null);
    try { const r = await restoreBackup(slug, env, pending.stamp, true); setDone(r.ok ? `Restored ${r.stamp} (a backup of before was taken: ${r.before})` : r.error ?? "failed"); setPending(null); reload(); }
    catch (e: any) { setError(String(e?.message ?? e)); }
    finally { setBusy(null); }
  }

  const verdict = ledger ? nightVerdict(ledger.last) : null;
  return (
    <div className="flex h-full w-full flex-col bg-bg">
      {/* ONE THIN ROW.
          Gone: the "Backups" heading (the tab says it) and the sentence describing the policy — every
          night, before every publication, encrypted, drilled. All true, and none of it changes what the
          operator does on this screen; the night's verdict below says whether it actually WORKED, which
          is the part they need. A policy belongs in the docs, not above every visit.

          What stays is what acts: the environment switch and "Back up now". */}
      <header className="flex items-center justify-between gap-3 border-b border-border px-5 py-2">
        <div role="tablist" aria-label="Environment" className="flex items-center gap-1">
          {(["dev", "prod"] as const).map((e) => (
            <button key={e} type="button" role="tab" aria-selected={env === e} onClick={() => { setEnv(e); setPending(null); setDone(null); }}
              className={["rounded-md px-2.5 py-1 text-[11px] transition", env === e ? "bg-accent text-white" : "text-text-secondary hover:bg-surface-muted hover:text-text"].join(" ")}>
              {e}
            </button>
          ))}
        </div>
        <button type="button" onClick={backupNow} disabled={busy !== null} className={BTN_PRIMARY}>{busy === "backup" ? "Backing up…" : "Back up now"}</button>
      </header>

      <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
        {verdict ? (
          <p className={["mb-3 text-xs", verdict.tone === "ok" ? "text-ok" : verdict.tone === "bad" ? "text-danger" : "text-text-tertiary"].join(" ")}>
            {verdict.tone === "ok" ? "✓ " : verdict.tone === "bad" ? "✗ " : ""}{verdict.text}
          </p>
        ) : null}
        {done ? <p className="mb-3 text-xs text-ok">{done}</p> : null}
        {error ? <p className="mb-3 text-xs text-danger">{error}</p> : null}
        {pending ? (
          <div className="mb-4 rounded-xl border border-warn-border bg-warn-soft p-4">
            <pre className="whitespace-pre-wrap text-[12px] text-warn">{pending.warning}</pre>
            <p className="mt-2 text-[11px] text-warn">The app is unavailable for the length of the restore (seconds) and says so.</p>
            <div className="mt-3 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setPending(null)} className={BTN_GHOST} disabled={busy !== null}>Cancel</button>
              <button type="button" onClick={confirmRestore} className={BTN_DANGER} disabled={busy !== null}>{busy === pending.stamp ? "Restoring…" : "Restore"}</button>
            </div>
          </div>
        ) : null}
        {!ledger && !error ? <p className="text-xs text-text-tertiary">Loading…</p>
          : ledger && ledger.backups.length === 0 ? <p className="text-xs text-text-tertiary">No backup yet for {env}. The first one is taken tonight — or now, with the button.</p>
          : ledger ? (
            <ul className="mx-auto flex max-w-2xl flex-col gap-2">
              {ledger.backups.map((b: BackupManifest) => (
                <li key={b.stamp} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-surface px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm text-text">{backupWhen(b.taken_at)} <span className="ml-2 rounded-full bg-surface-muted px-2 py-px text-[10px] uppercase tracking-wide text-text-secondary">{REASON_LABEL[b.reason]}</span></div>
                    <div className="mt-0.5 text-[11px] text-text-tertiary">{userRows(b.tables)} rows · {b.files.count} file(s) · <span className="font-mono">{b.stamp}</span></div>
                  </div>
                  <button type="button" onClick={() => askRestore(b.stamp)} disabled={busy !== null} className={BTN_GHOST}>{busy === b.stamp ? "…" : "Restore"}</button>
                </li>
              ))}
            </ul>
          ) : null}
      </div>
    </div>
  );
}

const BTN_PRIMARY = "rounded-full bg-accent px-3 py-1 text-xs font-medium text-white transition hover:bg-accent-hover disabled:opacity-50";
const BTN_GHOST   = "rounded-full border border-border px-3 py-1 text-xs text-text-secondary transition hover:border-border-active hover:text-text disabled:opacity-50";
const BTN_DANGER  = "rounded-full bg-danger-strong px-3 py-1 text-xs font-medium text-white transition hover:bg-danger-hover disabled:opacity-50";
